import {
  Button,
  Dropdown,
  InputField,
  Layout,
  Overlay,
  Spinner,
  StepCounter,
  Typography,
  useMobile,
  useMobileAndTablet,
} from '@sonnen/web-ui';
import { reportToSentry } from '@sonnen/web-utils';
import clsx from 'clsx';
import { useActionState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppContext } from '../../../AppContext/AppContext';
import HoneypotField from '../../../components/HoneypotField/HoneypotField';
import { InfoBanner } from '../../../components/InfoBanner/InfoBanner';
import { buildPvInstallationInput } from '../../../components/PVSystemForm/PVSystemForm.helpers';
import {
  AppRoute,
  EMPTY_DELIVERY_ADDRESS,
  PREDEFINED_CONSUMPTION,
  REFRESH_OFFER_EVENT,
} from '../../../constants';
import {
  CreateProductConfigurationsInput,
  CurrentUserSite,
  ProductConfigurations,
  useCreateBatteryOptimizationConfigurationMutation,
  useCreateProductConfigurationsMutation,
} from '../../../graphql/generated';
import { useCurrentRoute } from '../../../hooks/useCurrentRoute';
import { useValidation } from '../../../hooks/useValidation';
import { LocaleKey, useTranslation } from '../../../i18n/i18n';
import { useOfferContext } from '../../../OfferContext/OfferContext';
import { addBreadcrumb, reportException } from '../../../tools/sentry';
import { CurrentUser, ModuleName, OfferErrorTranslationKey, OrderFlow } from '../../../types';
import {
  getBatteryId,
  getContractCreationAllowed,
  getContractCreationBlocked,
  getGridPurchase,
  getOfferErrorCode,
  getValidDeliveryAddress,
  siteToOptionType,
} from '../../../utils/utils';
import {
  addressValidator,
  consumptionValidator,
  productConfigurationAddressValidator,
} from '../../../utils/validation';
import AdditionalAddress from './AdditionalAddress/AdditionalAddress';
import styles from './OfferCalculationForm.module.css';

const I18N_SCOPE = 'home.calculationForm';

type LayoutWrapperProps = {
  withLayout: boolean;
  children: React.ReactNode;
};

const LayoutWrapper = ({ withLayout, children }: LayoutWrapperProps) =>
  withLayout ? <Layout backgroundColor="none">{children}</Layout> : children;

const scrollToOfferCard = () => {
  const offerCardYOffset = document.getElementById('offer-card')?.offsetTop || 0;
  if (offerCardYOffset > window.innerHeight)
    window.scrollTo({ top: offerCardYOffset - 120, behavior: 'smooth' });
};

type OfferCalculationFormProps = {
  withoutLayout?: boolean;
};

const OfferCalculationForm = ({ withoutLayout = false }: OfferCalculationFormProps = {}) => {
  const { t } = useTranslation();
  const currentRoute = useCurrentRoute();
  const mobile = useMobile();
  const tablet = useMobileAndTablet();
  const homePage = currentRoute === AppRoute.ROOT;
  const eligibilityCheckPage = currentRoute === AppRoute.ELIGIBILITY_CHECK;
  const formRef = useRef<HTMLFormElement>(null);
  const submitPerfPendingRef = useRef(false);

  const {
    formValues,
    offerRequiresManualAddress,
    orderFlow,
    selectedSite,
    setFormValues,
    setOfferRequiresManualAddress,
    setSelectedSite,
    user,
    addModule,
    userProcessing,
  } = useAppContext();

  const {
    offer,
    offerError,
    productConfigurationsInput,
    setProductConfigurationsInput,
    personsInHousehold,
    setPersonsInHousehold,
    setOfferLoading,
    setOfferError,
    workEmail,
    setOffer,
    pvSystemData,
  } = useOfferContext();

  const [
    createProductConfigurationsMutation,
    {
      data: productConfigurationsData,
      error: productConfigurationsError,
      loading: productConfigurationsLoading,
    },
  ] = useCreateProductConfigurationsMutation();

  const [
    createBatteryOptimizationConfigurationMutation,
    {
      data: batteryOptimizationData,
      error: batteryOptimizationError,
      loading: batteryOptimizationLoading,
    },
  ] = useCreateBatteryOptimizationConfigurationMutation();

  const loading = batteryOptimizationLoading || productConfigurationsLoading;

  const markPerformance = (name: string) => {
    if (typeof performance !== 'undefined' && performance?.mark) {
      performance.mark(name);
    }
  };

  const measurePerformance = (name: string, start: string, end: string) => {
    if (typeof performance !== 'undefined' && performance?.measure) {
      try {
        performance.measure(name, start, end);
      } catch {
        // Ignore if marks are missing
      }
    }
  };

  useEffect(() => {
    setOfferLoading(loading);
  }, [loading]);

  const createConfigurations = (input: CreateProductConfigurationsInput) => {
    if (orderFlow === OrderFlow.batteryOptimization) {
      createBatteryOptimizationConfigurationMutation({
        variables: { input: { ...input, workEmail, expectedElectricityConsumptionInKwh: null } },
      }).catch(() => {
        // Error is handled through batteryOptimizationError state
      });
      return;
    }

    if (
      !input.expectedElectricityConsumptionInKwh ||
      isNaN(input.expectedElectricityConsumptionInKwh)
    ) {
      reportToSentry('Skipping configuration creation - missing consumption', {
        tag: 'offer_calculation',
        level: 'warning',
        context: {
          component: 'OfferCalculationForm',
          event: 'mutation_skipped',
          orderFlow,
          siteId: input.siteId,
          reason: 'missing_or_invalid_consumption',
          consumption: input.expectedElectricityConsumptionInKwh,
        },
      });
      return;
    }

    const pvInstallation = buildPvInstallationInput(pvSystemData ?? undefined, false);

    createProductConfigurationsMutation({
      variables: {
        input: {
          ...input,
          workEmail,
          ...(pvInstallation ? { pvInstallation } : {}),
        },
      },
    }).catch(() => {
      // Error is handled through productConfigurationsError state
    });
  };

  const getProductConfigurationsInput = (
    user: CurrentUser,
    site: CurrentUserSite | null,
  ): CreateProductConfigurationsInput => {
    const deliveryAddress = user
      ? getValidDeliveryAddress(user, site)
      : productConfigurationsInput.deliveryAddress;
    const siteGridPurchase = getGridPurchase(site);
    const batteryId = getBatteryId(site);
    const userWithoutSite = Boolean(user?.sites && !user.sites.length);
    const consumption =
      siteGridPurchase || (userWithoutSite ? PREDEFINED_CONSUMPTION[2] : false) || NaN;

    const input: CreateProductConfigurationsInput = {
      expectedElectricityConsumptionInKwh: consumption,
      deliveryAddress: { ...deliveryAddress, postalCode: deliveryAddress.postalCode || '' },
      siteId: site?.id,
      workEmail,
      ...(batteryId && { batteryId }),
    };

    return input;
  };

  useEffect(() => {
    if (!user) return;

    setOfferRequiresManualAddress(false);

    if (!offer) {
      const newInput = getProductConfigurationsInput(user, selectedSite);
      setProductConfigurationsInput(newInput);
      setFormValues((previousValues) => ({
        ...previousValues,
        deliveryAddress: { ...newInput.deliveryAddress },
      }));

      if (!selectedSite) {
        return;
      }

      const siteIsAllowedToCreateContract = getContractCreationAllowed(selectedSite);
      if (siteIsAllowedToCreateContract) {
        createConfigurations(newInput);
      }
    }
  }, [user, selectedSite]);

  useEffect(() => {
    const data = productConfigurationsData || batteryOptimizationData;
    const error = productConfigurationsError || batteryOptimizationError;

    if (error) {
      const errorMessage = error.graphQLErrors[0]?.message || 'Unknown error';
      const errorCode = error.graphQLErrors[0]?.extensions?.code;

      // Add breadcrumb before reporting error
      addBreadcrumb('Offer calculation failed', 'api', 'error', {
        errorMessage,
        errorCode,
        orderFlow,
        siteId: selectedSite?.id,
      });

      // Use reportException for the actual error
      reportException(error, {
        tag: 'offer_calculation',
        context: {
          component: 'OfferCalculationForm',
          event: 'graphql_error',
          errorMessage,
          errorCode,
          orderFlow,
          siteId: selectedSite?.id,
          deliveryAddress: productConfigurationsInput.deliveryAddress,
          isEligibilityCheck: eligibilityCheckPage,
        },
      });

      // Only report address-related errors separately for additional context
      if (errorMessage.includes('Delivery address') || errorMessage.includes('address')) {
        reportToSentry('Delivery address validation error', {
          tag: 'offer_calculation',
          level: 'error',
          context: {
            component: 'OfferCalculationForm',
            event: 'address_error',
            errorMessage,
            orderFlow,
            siteId: selectedSite?.id,
          },
        });
      }

      setOffer(null);
      setOfferError(getOfferErrorCode(errorCode));
    }

    if (data) {
      const result = data.createProductConfigurations as ProductConfigurations;

      setOfferError(null);
      setOffer(result);

      if (
        productConfigurationsData &&
        productConfigurationsData.createProductConfigurations.energyDynamic
          ?.configurationWithBatteryOptimizationModule
      ) {
        addModule(ModuleName.PRICE_OPTIMIZATION);
      }

      if (
        productConfigurationsData &&
        productConfigurationsData.createProductConfigurations.energyFix
          ?.configurationWithFeedInModule
      ) {
        addModule(ModuleName.DIRECT_MARKETING);
      }
    }
  }, [
    productConfigurationsData,
    productConfigurationsError,
    batteryOptimizationData,
    batteryOptimizationError,
    user,
    selectedSite?.id,
  ]);

  const refreshOffer = useCallback(() => {
    setOffer(null);
    setOfferError(null);
    createConfigurations({
      ...productConfigurationsInput,
      deliveryAddress: formValues.deliveryAddress,
    });
  }, [formValues, user, selectedSite?.id, orderFlow]);

  useEffect(() => {
    window.addEventListener(REFRESH_OFFER_EVENT, refreshOffer);
    return () => {
      window.removeEventListener(REFRESH_OFFER_EVENT, refreshOffer);
    };
  }, []);

  const { expectedElectricityConsumptionInKwh } = productConfigurationsInput;
  const consumption = expectedElectricityConsumptionInKwh || 0;

  const contractCreationBlocked = getContractCreationBlocked(
    selectedSite,
    (productConfigurationsData || batteryOptimizationData)
      ?.createProductConfigurations as ProductConfigurations,
    orderFlow,
  );

  const [validate, { errors, setErrors }] = useValidation({
    lazy: true,
    values: productConfigurationsInput,
    schema: {
      deliveryAddress: {
        required: true,
        message: t(`${I18N_SCOPE}.postcode.error`),
        validator: productConfigurationAddressValidator,
      },
      expectedElectricityConsumptionInKwh: {
        required: orderFlow !== OrderFlow.batteryOptimization,
        message: t(`${I18N_SCOPE}.consumption.${consumption > 0 ? 'error' : 'missing'}`),
        validator: consumptionValidator,
      },
    },
  });

  const { sites } = user || {};
  const gridPurchase: number = useMemo(() => getGridPurchase(selectedSite), [selectedSite]);
  const showStepper = !(
    userProcessing ||
    gridPurchase ||
    sites?.length ||
    orderFlow === OrderFlow.batteryOptimization
  );

  const submitButtonDisabled = Boolean(
    errors.expectedElectricityConsumptionInKwh ||
      (offerRequiresManualAddress && !addressValidator(formValues.deliveryAddress)),
  );

  const onBatteryOptimizationPage = currentRoute === AppRoute.BATTERY_OPTIMIZATION;
  const onTariffsComparisonPage = currentRoute === AppRoute.TARIFFS_COMPARISON;
  const tariffOverviewPage = currentRoute === AppRoute.TARIFF_OVERVIEW;
  const shouldWrapWithLayout = !withoutLayout && !homePage && !eligibilityCheckPage;
  const showAdditionalAddress =
    offerRequiresManualAddress &&
    (onBatteryOptimizationPage ||
      onTariffsComparisonPage ||
      tariffOverviewPage ||
      eligibilityCheckPage ||
      homePage);

  const showSiteDropdown = sites && sites.length > 1 && !tariffOverviewPage;

  // Check if consumption input is the only field shown (for eligibility check page spacing)
  const isConsumptionOnlyField =
    eligibilityCheckPage && user && !showSiteDropdown && !showStepper && !showAdditionalAddress;

  const onExpectedElectricityConsumptionChange = (_: string, value: string) => {
    setProductConfigurationsInput({
      ...productConfigurationsInput,
      expectedElectricityConsumptionInKwh: parseInt(value, 10),
    });
  };

  const onPostalCodeChange = (_: string, postalCode: string) => {
    setOfferRequiresManualAddress(false);
    setFormValues((previousValues) => ({
      ...previousValues,
      deliveryAddress: {
        ...EMPTY_DELIVERY_ADDRESS,
        postalCode,
      },
    }));

    setProductConfigurationsInput({
      ...productConfigurationsInput,
      deliveryAddress: {
        ...productConfigurationsInput.deliveryAddress,
        postalCode,
      },
    });

    setErrors({ ...errors, deliveryAddress: '' });
  };

  const onSelectedSiteChange = (_: string, value: string) => {
    const newSite = sites?.find((site) => site.id === value);

    setOffer(null);
    setOfferError(null);
    setSelectedSite(newSite || null);

    if (!user || !newSite) {
      return false;
    }

    const newInput = getProductConfigurationsInput(user, newSite);
    setProductConfigurationsInput(newInput);
    createConfigurations(newInput);
  };

  const onBlur = () => validate();

  const onStepCounterChange = (value: number) => {
    const newConsumption = PREDEFINED_CONSUMPTION[value] || expectedElectricityConsumptionInKwh;
    setPersonsInHousehold(value);
    setProductConfigurationsInput({
      ...productConfigurationsInput,
      expectedElectricityConsumptionInKwh: newConsumption,
    });
  };

  const [, submitAction, isSubmitting] = useActionState(
    async () => {
      submitPerfPendingRef.current = true;
      markPerformance('offer_calc_submit_start');
      if (validate()) {
        createConfigurations({
          ...productConfigurationsInput,
          deliveryAddress: {
            ...productConfigurationsInput.deliveryAddress,
            city: formValues.deliveryAddress.city,
            streetName: formValues.deliveryAddress.streetName,
            streetNumber: formValues.deliveryAddress.streetNumber,
          },
        });
        return { status: 'submitted' };
      }

      return { status: 'invalid' };
    },
    { status: 'idle' },
  );

  const onKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') formRef.current?.requestSubmit();
  };

  useEffect(() => {
    if (expectedElectricityConsumptionInKwh) {
      validate(['expectedElectricityConsumptionInKwh']);
    }
  }, [expectedElectricityConsumptionInKwh]);

  useEffect(() => {
    if (!submitPerfPendingRef.current) return;

    const hasResult = Boolean(
      (!loading && (productConfigurationsData || batteryOptimizationData)) ||
        productConfigurationsError ||
        batteryOptimizationError,
    );

    if (!hasResult) return;

    markPerformance('offer_calc_submit_end');
    measurePerformance(
      'offer_calc_submit_to_response',
      'offer_calc_submit_start',
      'offer_calc_submit_end',
    );
    submitPerfPendingRef.current = false;
  }, [
    loading,
    productConfigurationsData,
    batteryOptimizationData,
    productConfigurationsError,
    batteryOptimizationError,
  ]);

  useEffect(() => {
    if (!offerError) return;

    if (offerError === OfferErrorTranslationKey.POSTCODE_REQUIRES_ADDRESS) {
      setOfferRequiresManualAddress(true);
    }

    if (offerError === OfferErrorTranslationKey.INVALID_CUSTOMER_ADDRESS) {
      setOfferRequiresManualAddress(true);
      setFormValues((previousValues) => ({
        ...previousValues,
        deliveryAddress: {
          ...previousValues.deliveryAddress,
          city: '',
          streetName: '',
          streetNumber: '',
        },
      }));
    }
  }, [offerError]);

  useEffect(() => {
    const images = document.querySelectorAll('img');
    if (tablet && offer && !mobile) {
      scrollToOfferCard();
      images.forEach((img) => img.addEventListener('load', scrollToOfferCard));
    }

    return () => images.forEach((img) => img.removeEventListener('load', scrollToOfferCard));
  }, [offer]);

  let submitButtonLabel: LocaleKey = `${I18N_SCOPE}.submitBtn`;
  if (eligibilityCheckPage) {
    submitButtonLabel = 'eligbilityCheck.stepper.continue';
  } else if (homePage) {
    submitButtonLabel = 'login.visitorCard.calculateAsGuestButton';
  } else if (orderFlow === OrderFlow.batteryOptimization) {
    submitButtonLabel = 'batteryOptimization.checkAvailability';
  }

  return (
    <>
      <section
        data-testid="offer-calculation-form"
        className={clsx(styles.offerCalculationForm, {
          [styles.homePagePadding]: homePage,
          [styles.eligibilityCheckPadding]: eligibilityCheckPage,
        })}
      >
        {userProcessing && (
          <Overlay className={styles.overlayContent} roundedCorners>
            <Spinner size="xl" accessibilityLabel={t('loadingUserDetails')} />
            <Typography.Body1>{t(`${I18N_SCOPE}.userLoadingText`)}</Typography.Body1>
          </Overlay>
        )}

        <LayoutWrapper withLayout={shouldWrapWithLayout}>
          {offerError &&
            ![
              OfferErrorTranslationKey.POSTCODE_REQUIRES_ADDRESS,
              OfferErrorTranslationKey.INVALID_CUSTOMER_ADDRESS,
            ].includes(offerError) &&
            !contractCreationBlocked && (
              <div className={styles.configError} data-testid="config-error">
                <Typography.Label2 variant="alert">{t(`offer.${offerError}`)}</Typography.Label2>
              </div>
            )}

          <form
            ref={formRef}
            action={submitAction}
            className={styles.formContent}
            onReset={(e) => e.preventDefault()}
          >
            {!user && !userProcessing && (
              <span className={styles.growChild}>
                <InputField
                  dataTestId="postcode"
                  error={errors.deliveryAddress}
                  inputMode="numeric"
                  label={t(`${I18N_SCOPE}.postcode.label`)}
                  maxLength={5}
                  name="postalCode"
                  onBlur={onBlur}
                  onChange={onPostalCodeChange}
                  readOnly={loading}
                  expanded
                  type="text"
                  autoComplete="on"
                  value={productConfigurationsInput.deliveryAddress.postalCode || ''}
                  onKeyUp={onKeyUp}
                />
              </span>
            )}

            {showSiteDropdown && (
              <span className={styles.growChild}>
                <Dropdown
                  disabled={loading}
                  name="selectedSite"
                  label={t(`${I18N_SCOPE}.selectSite`)}
                  options={sites.map((site) => siteToOptionType(site, t('battery'), t('contract')))}
                  required
                  dataTestId="site-selection"
                  onChange={onSelectedSiteChange}
                  searchable={false}
                  value={selectedSite ? siteToOptionType(selectedSite) : undefined}
                />
              </span>
            )}

            {showStepper && (
              <span className={clsx(styles.growChild, styles.stepper)}>
                <StepCounter
                  disabled={loading}
                  icon="User"
                  label={t(`${I18N_SCOPE}.stepperLabel`)}
                  max={8}
                  min={1}
                  onChange={onStepCounterChange}
                  value={personsInHousehold}
                  ariaLabel={t(`${I18N_SCOPE}.stepperAriaLabel`)}
                  ariaLabelMinus={t(`${I18N_SCOPE}.stepperAriaLabelMinus`)}
                  ariaLabelPlus={t(`${I18N_SCOPE}.stepperAriaLabelPlus`)}
                />
              </span>
            )}

            {!contractCreationBlocked && (
              <>
                {orderFlow !== OrderFlow.batteryOptimization && (
                  <span className={styles.growChild}>
                    <InputField
                      dataTestId="consumption"
                      error={errors.expectedElectricityConsumptionInKwh}
                      inputMode="numeric"
                      label={t(
                        `${I18N_SCOPE}.consumption.${selectedSite ? 'labelForExistingSite' : 'label'}`,
                      )}
                      maxLength={7}
                      name="expectedElectricityConsumptionInKwh"
                      onChange={onExpectedElectricityConsumptionChange}
                      readOnly={loading}
                      expanded
                      suffix="kWh"
                      onBlur={onBlur}
                      onKeyUp={onKeyUp}
                      value={
                        !Number.isNaN(expectedElectricityConsumptionInKwh)
                          ? consumption.toString()
                          : ''
                      }
                      disabled={Boolean(contractCreationBlocked)}
                    />

                    {Boolean(gridPurchase) && (
                      <div className={styles.consumptionInfo}>
                        <InfoBanner variant="secondary" iconSize="md">
                          <Typography.Label2 variant="secondary">
                            {t(`${I18N_SCOPE}.consumption.info`, { value: gridPurchase })}
                          </Typography.Label2>
                        </InfoBanner>
                      </div>
                    )}
                  </span>
                )}

                {showAdditionalAddress && !contractCreationBlocked && (
                  <AdditionalAddress
                    onBlur={onBlur}
                    expandedInputs={eligibilityCheckPage || withoutLayout}
                  />
                )}

                <HoneypotField />

                {!contractCreationBlocked && (
                  <span
                    className={clsx(styles.growChild, {
                      [styles.compactButton]: eligibilityCheckPage,
                      [styles.consumptionOnlySpacing]: isConsumptionOnlyField,
                    })}
                  >
                    <Button
                      type="submit"
                      label={t(submitButtonLabel)}
                      variant={eligibilityCheckPage ? 'primary' : 'secondary'}
                      dataTestId="calculate-price-btn"
                      loading={loading || isSubmitting}
                      disabled={submitButtonDisabled || isSubmitting}
                      iconRight="ArrowRight"
                      expanded={!eligibilityCheckPage}
                    />
                  </span>
                )}
              </>
            )}
          </form>
        </LayoutWrapper>
      </section>
    </>
  );
};

export default OfferCalculationForm;
